/**
 * @file SwipeButton.tsx
 * @brief Slide-to-confirm CTA — web parity for mobile `VZSwipeButton`.
 *
 * @details
 * Brand track + floating pill thumb. Drag past ~70% to confirm; incomplete
 * swipes spring back. Keyboard / screen-reader users activate via Enter/Space
 * (same accessibility pattern as mobile VoiceOver “activate”).
 *
 * Dark theme (DES 08-10-2026, phone `darkSlide`): the track is THE dock
 * glass (shared/theme/dockGlass.ts), brand-red thumb, white glyph,
 * primary-text label, brand shimmer (index.css). Light is unchanged.
 *
 * @author DES
 * @date 28-07-2026
 * @updated 08-10-2026 — dark glass slide
 */

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { ArrowRight, LoaderCircle, type LucideIcon } from "lucide-react";
import { cn } from "@/shared/theme/cn";
import { dockGlassDark } from "@/shared/theme/dockGlass";

/** Default slide, and `compact` for the recorder dock (DES 11-10-2026). */
const SIZES = {
  default: { height: 68, margin: 6, thumbWidth: 100, gap: 16 },
  compact: { height: 40, margin: 3, thumbWidth: 54, gap: 12 },
  /** The desktop widget's one-row dock (DES 11-10-2026). */
  mini: { height: 36, margin: 3, thumbWidth: 52, gap: 10 },
} as const;
const SWIPE_THRESHOLD = 0.7;

export interface SwipeButtonProps {
  text: string;
  onSwipeComplete: () => void;
  disabled?: boolean;
  loading?: boolean;
  /** Label while `loading` (default: "Starting…") */
  loadingText?: string;
  icon?: LucideIcon;
  className?: string;
  accessibilityLabel?: string;
  accessibilityHint?: string;
  /** "compact": 40px tall (the recorder dock); "mini": 36px (the desktop widget). */
  size?: keyof typeof SIZES;
}

export function SwipeButton({
  text,
  onSwipeComplete,
  disabled = false,
  loading = false,
  loadingText = "Starting…",
  icon: Icon = ArrowRight,
  className,
  accessibilityLabel,
  accessibilityHint,
  size = "default",
}: SwipeButtonProps) {
  const {
    height: BUTTON_HEIGHT,
    margin: THUMB_MARGIN,
    thumbWidth: THUMB_WIDTH,
    gap: LABEL_GAP,
  } = SIZES[size];
  const THUMB_HEIGHT = BUTTON_HEIGHT - THUMB_MARGIN * 2;
  const compact = size !== "default";
  const mini = size === "mini";
  const trackRef = useRef<HTMLDivElement>(null);
  const [trackWidth, setTrackWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [completed, setCompleted] = useState(false);
  const dragStartX = useRef(0);
  const offsetAtStart = useRef(0);
  const offsetRef = useRef(0);
  const completedRef = useRef(false);
  const labelId = useId();

  const locked = disabled || loading || completed;
  const maxTranslate = Math.max(
    0,
    trackWidth - THUMB_WIDTH - THUMB_MARGIN * 2,
  );

  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  useEffect(() => {
    completedRef.current = completed;
  }, [completed]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const measure = () => setTrackWidth(el.getBoundingClientRect().width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const finishSwipe = useCallback(() => {
    if (completedRef.current || disabled || loading) return;
    setCompleted(true);
    completedRef.current = true;
    setOffset(maxTranslate);
    onSwipeComplete();
    window.setTimeout(() => {
      setOffset(0);
      setCompleted(false);
      completedRef.current = false;
    }, 600);
  }, [disabled, loading, maxTranslate, onSwipeComplete]);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (locked || maxTranslate <= 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStartX.current = e.clientX;
    offsetAtStart.current = offsetRef.current;
    setDragging(true);
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragging || locked) return;
    const dx = e.clientX - dragStartX.current;
    const next = Math.max(
      0,
      Math.min(maxTranslate, offsetAtStart.current + dx),
    );
    setOffset(next);
  };

  const onPointerUp = () => {
    if (!dragging) return;
    setDragging(false);
    const threshold = maxTranslate * SWIPE_THRESHOLD;
    if (offsetRef.current >= threshold) {
      finishSwipe();
    } else {
      setOffset(0);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (locked) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      finishSwipe();
    }
  };

  const progress = maxTranslate > 0 ? offset / maxTranslate : 0;
  const textOpacity = Math.max(0, 1 - progress / 0.4);

  const thumbStyle: CSSProperties = {
    width: THUMB_WIDTH,
    height: THUMB_HEIGHT,
    left: THUMB_MARGIN,
    top: "50%",
    transform: `translate3d(${offset}px, -50%, 0)`,
    transition: dragging
      ? "none"
      : "transform 280ms cubic-bezier(0.34, 1.2, 0.64, 1)",
  };

  return (
    <div
      ref={trackRef}
      role="button"
      tabIndex={locked ? -1 : 0}
      aria-labelledby={labelId}
      aria-label={accessibilityLabel ?? text}
      aria-describedby={accessibilityHint ? `${labelId}-hint` : undefined}
      aria-disabled={locked || undefined}
      aria-busy={loading || undefined}
      onKeyDown={onKeyDown}
      className={cn(
        "relative w-full select-none overflow-hidden rounded-full bg-brand shadow-sm",
        // Dark: every slide is THE dock glass track (phone VZSwipeButton
        // `darkSlide`, DES 08-10-2026); light keeps the brand track.
        dockGlassDark,
        "outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        locked && "opacity-70",
        className,
      )}
      style={{ height: BUTTON_HEIGHT }}
    >
      {/* Shimmer sweep */}
      {!locked ? (
        <div
          className="pointer-events-none absolute inset-0 overflow-hidden"
          aria-hidden
        >
          <div className="vz-swipe-shimmer absolute inset-y-0 w-full opacity-60" />
        </div>
      ) : null}

      <div
        className="pointer-events-none absolute inset-0 z-[1] flex items-center"
        style={{
          opacity: textOpacity,
          paddingLeft: THUMB_WIDTH + THUMB_MARGIN + LABEL_GAP,
          paddingRight: THUMB_MARGIN,
          transition: dragging ? "none" : "opacity 180ms ease-out",
        }}
      >
        <span
          id={labelId}
          className={cn(
            "truncate font-bold text-brand-foreground dark:text-app-text",
            mini ? "text-xs" : compact ? "text-sm" : "text-lg",
          )}
        >
          {loading ? loadingText : text}
        </span>
      </div>
      {accessibilityHint ? (
        <span id={`${labelId}-hint`} className="sr-only">
          {accessibilityHint}
        </span>
      ) : null}

      <div
        role="presentation"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className={cn(
          // Dark: brand-red thumb, white glyph (DES 08-10-2026).
          "absolute z-[2] flex cursor-grab items-center justify-center rounded-full bg-secondary shadow-md dark:bg-brand",
          "active:cursor-grabbing touch-none",
          locked && "cursor-not-allowed",
        )}
        style={thumbStyle}
      >
        {loading ? (
          <LoaderCircle
            className={cn("animate-spin text-foreground dark:text-white", mini ? "h-4 w-4" : compact ? "h-5 w-5" : "h-9 w-9")}
          />
        ) : (
          <Icon
            className={cn("text-foreground dark:text-white", mini ? "h-4 w-4" : compact ? "h-5 w-5" : "h-9 w-9")}
            strokeWidth={1.75}
          />
        )}
      </div>
    </div>
  );
}
